import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';

interface IncDecCounterProps {
  value: number;
  min?: number;
  onChange: (value: number) => void;
}

export const IncDecCounter = (props: IncDecCounterProps) => {
  const { onChange } = props;
  const [value, setValue] = useState<number>(props.value);
  const [decDisabled, setDecDisabled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const min = props.min ?? 0;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value.toString();
    }
    if (value <= min) {
      setDecDisabled(true);
    }
  }, [min, value]);

  const onIncrease = useCallback(() => {
    const newValue = value + 1;
    setValue(newValue);
    onChange(newValue);
    if (value === min) {
      setDecDisabled(false);
    }
  }, [value, onChange, min]);

  const onDecrease = useCallback(() => {
    const newValue = value - 1;
    if (newValue <= min) {
      setDecDisabled(true);
    }
    if (newValue < min) {
      return;
    }
    setValue(newValue);
    onChange(newValue);
  }, [value, min, onChange]);

  const handleInput = useCallback(() => {
    let newValue = parseInt(inputRef.current?.value.replace(/[^\d]{1,}/g, '') || min.toString(), 10);
    if (isNaN(newValue)) {
      return;
    }
    if (newValue < min) {
      newValue = min;
    }
    setValue(newValue);
    onChange(newValue);
    if (newValue <= min) {
      setDecDisabled(true);
    }
    if (value === min && newValue > min) {
      setDecDisabled(false);
    }
    if (newValue <= min || inputRef.current?.value.match(/[\d]{1,}/g)) {
      if (inputRef.current) {
        inputRef.current.value = newValue.toString();
      }
    }
  }, [min, onChange, value]);

  return (
    <div className="counter-inc-dec flex border border-neutral-200 rounded-lg">
      <button
        className="inc-dec-counter__dec w-8 flex items-center justify-center rounded-l-lg group"
        onClick={onDecrease}
      >
        <span
          className={classNames('inline-block p-[4px] rounded', {
            'group-hover:bg-neutral-200': !decDisabled,
          })}
        >
          <Icon
            icon={IconsMap.MINUS}
            className={classNames('w-[16px]', {
              'text-neutral-500': !decDisabled,
              'text-neutral-300': decDisabled,
            })}
          />
        </span>
      </button>
      <input
        ref={inputRef}
        type="text"
        className="text-center border-l border-r border-l-neutral-200 border-r-neutral-200 w-[34px] h-8 outline-none flex-shrink-0 text-sm text-neutral-800 caret-teal-500"
        onInput={handleInput}
      />
      <button
        className="inc-dec-counter__inc w-8 flex items-center justify-center rounded-r-lg group"
        onClick={onIncrease}
      >
        <span className={classNames('inline-block p-[4px] rounded group-hover:bg-neutral-200')}>
          <Icon icon={IconsMap.PLUS} className="w-[16px] text-neutral-500" />
        </span>
      </button>
    </div>
  );
};
